export type AppSurface = 'valentine' | 'valentine_tasks';

export interface SurfaceConfig {
  basename: string;
  taskBase: string;
  settings: string;
  workerUrl: string;
  workerScope: string;
}

export const SURFACES = {
  valentine: {
    basename: '/',
    taskBase: '/tasks',
    settings: '/settings',
    workerUrl: '/sw.js',
    workerScope: '/',
  },
  valentine_tasks: {
    basename: '/tasks-app/',
    taskBase: '/tasks-app/tasks',
    settings: '/tasks-app/settings',
    workerUrl: '/tasks-app/sw.js',
    workerScope: '/tasks-app/',
  },
} as const satisfies Record<AppSurface, SurfaceConfig>;

export function surfaceConfig(surface: AppSurface): SurfaceConfig {
  return SURFACES[surface];
}

export const taskPath = (surface: AppSurface, id: number) => `${SURFACES[surface].taskBase}/${id}`;

export const settingsPath = (surface: AppSurface) => SURFACES[surface].settings;

export function appSurfaceForPath(pathname: string): AppSurface {
  return pathname === '/tasks-app' || pathname.startsWith('/tasks-app/')
    ? 'valentine_tasks'
    : 'valentine';
}

function decodedPathIsSafe(raw: string): boolean {
  let decoded = raw.split(/[?#]/, 1)[0];
  for (let depth = 0; depth < 10; depth += 1) {
    let next: string;
    try {
      next = decodeURIComponent(decoded);
    } catch {
      return false;
    }
    decoded = next;
    if (!/%[0-9a-f]{2}/i.test(decoded)) break;
  }
  if (/%[0-9a-f]{2}/i.test(decoded)) return false;
  const hasControlCharacter = Array.from(decoded).some(character => {
    const code = character.charCodeAt(0);
    return code <= 0x1f || code === 0x7f;
  });
  if (decoded.includes('\\') || decoded.startsWith('//') || hasControlCharacter) return false;
  return !decoded.split('/').some(segment => segment === '.' || segment === '..');
}

/**
 * Allows an in-app path after authentication while refusing an external or
 * protocol-relative URL that could otherwise become an open redirect.
 */
export function validateReturnPath(
  raw: string | null | undefined,
  surface?: AppSurface,
): string | null {
  if (
    !raw
    || !raw.startsWith('/')
    || raw.startsWith('//')
    || raw.includes('\\')
    || !decodedPathIsSafe(raw)
  ) return null;

  try {
    const appOrigin = 'https://valentine.invalid';
    const target = new URL(raw, appOrigin);
    if (target.origin !== appOrigin) return null;
    const inCompanion = target.pathname === '/tasks-app' || target.pathname.startsWith('/tasks-app/');
    if (surface === 'valentine_tasks' && !inCompanion) return null;
    if (surface === 'valentine' && inCompanion) return null;
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return null;
  }
}
